import React, { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  setDictionaryListAction,
  updateDictionaryItemAction,
} from '../../../redux/slices/dictionary';
import { DictionaryTypeEnum } from '../../../utils/interfaces';
import { GET_DICTIONARY_BASIC_EVENTS } from '../../../utils/apollo/basicEvents/query';
import {
  IBasicEventRenameMutation,
  IBasicEventRenameMutationVariables,
  IBasicEventToggleMutation,
  IBasicEventToggleMutationVariables,
  IBasicEventUpdateDescriptionMutation,
  IBasicEventUpdateDescriptionMutationVariables,
  IDictionaryBasicEventFieldsFragment,
  IGetDictionaryBasicEventsQuery,
  IGetDictionaryBasicEventsQueryVariables,
  PermissionKey,
} from '../../../utils/apollo/graphqlSchema';
import {
  BASIC_EVENT_RENAME,
  BASIC_EVENT_TOGGLE,
  BASIC_EVENT_UPDATE_DESCRIPTION,
} from '../../../utils/apollo/basicEvents/mutation';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import { assertNotNil } from '../../../utils/types';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import DictionaryHeader from '../utils/DictionaryHeader';
import usePermissionSchemas from '../../PermissionControls/hooks/usePermissionSchemas';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import { useBootstrapSelector } from '../../../redux/selectorHooks';
import { useDebounce } from '../../../utils/hooks';
import toastMessage from '../../../utils/toastMessage';
import { ContentLoader } from '../../../components/Loaders';
import { eventInformationUpdated } from '../../../utils/analytics/events/event/events';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import DictionaryNoResults from '../DictionaryNoResults';
import { pageViewed } from '../../../utils/analytics/events/page/events';
import EventsTable from './EventsTable';
import useApplyTags from '../useApplyTags';
import { CategoryEntityType } from '../../../components/Categories/utils';
import {
  BASIC_EVENT_ADD_CATEGORY,
  BASIC_EVENT_REMOVE_CATEGORY,
} from '../../../utils/apollo/basicEvents/mutation';
import { ManagedEntityType } from '../../../utils/apollo/graphqlSchema';
import { RowSelectionState } from '@tanstack/react-table';
import { useGlobalApplyTags } from '../utils/useGlobalApplyTags';

const EventDictionary = () => {
  const events = useAppSelector(
    (state) => state.dictionary.event ?? [],
  ) as IDictionaryBasicEventFieldsFragment[];

  const dispatch = useAppDispatch();
  const { track } = useAnalytics();
  useEffect(() => {
    track(
      pageViewed({
        category: 'dictionary',
        name: 'event',
        id: null,
      }),
    );
  }, [track]);

  const preventBasicEventMetadataUpdates = useBootstrapSelector(
    'preventBasicEventMetadataUpdates',
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { selectedSchemaId, permittedSchemaIds } = usePermissionSchemas({
    requestedPermissionKey: PermissionKey.EventRead,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const { checkIsPermitted } = usePermission();
  const isPermittedToEditEvent = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.EventEdit],
    schemaId: selectedSchemaId,
  });
  const isPermittedToAssignCategory = checkIsPermitted({
    requiredPermissionKeys: [
      PermissionKey.CategoryCreate,
      PermissionKey.EventEdit,
    ],
    schemaId: selectedSchemaId,
  });

  const [renameEvent] = useMutation<
    IBasicEventRenameMutation,
    IBasicEventRenameMutationVariables
  >(BASIC_EVENT_RENAME);

  const onRenameEventModalConfirm = async (
    event: IDictionaryBasicEventFieldsFragment,
    newName: string,
  ) => {
    const res = await renameEvent({
      variables: {
        input: {
          schema: selectedSchemaId,
          key: event.key,
          name: newName,
        },
      },
    });
    if (printGraphqlErrors(res?.data?.basicEventRename?.errors)) {
      return;
    }
    assertNotNil(res.data);
    dispatch(
      updateDictionaryItemAction({
        item: { ...event, name: newName, isRenamed: event.key !== newName },
        dictionaryType: DictionaryTypeEnum.DictionaryEvent,
      }),
    );

    // update the name in the analytics event. Analytics event properties
    // always represent the state *after* the operation
    track(eventInformationUpdated({ ...event, name: newName }));

    toastMessage({ message: `${newName} has been renamed.`, type: 'info' });
  };

  // enable/disable
  const [toggleEvent] = useMutation<
    IBasicEventToggleMutation,
    IBasicEventToggleMutationVariables
  >(BASIC_EVENT_TOGGLE);

  const [updateBasicEventDescription] = useMutation<
    IBasicEventUpdateDescriptionMutation,
    IBasicEventUpdateDescriptionMutationVariables
  >(BASIC_EVENT_UPDATE_DESCRIPTION);

  const setEventStatusCallback = useCallback(
    async (event: IDictionaryBasicEventFieldsFragment, isDisabled: boolean) => {
      const res = await toggleEvent({
        variables: {
          input: {
            schema: selectedSchemaId,
            name: event.key,
            disabled: isDisabled,
          },
        },
      });
      if (printGraphqlErrors(res?.data?.basicEventToggle?.errors)) {
        return;
      }
      assertNotNil(res.data);
      dispatch(
        updateDictionaryItemAction({
          item: { ...event, isDisabled },
          dictionaryType: DictionaryTypeEnum.DictionaryEvent,
        }),
      );
      const message = `Event ${event.name} is ${
        isDisabled ? 'disabled' : 'enabled'
      }.`;
      toastMessage({ message, type: 'info' });
    },
    [dispatch, toggleEvent, selectedSchemaId],
  );

  const onFetchDictionaryBasicEvents = (
    res: IGetDictionaryBasicEventsQuery,
  ) => {
    const entities = res?.dictionaryBasicEvents?.values;
    if (entities && entities.length >= 0) {
      dispatch(
        setDictionaryListAction({
          newValues: entities,
          dictionaryType: DictionaryTypeEnum.DictionaryEvent,
        }),
      );
    }
  };

  const { loading: basicEventsLoading, refetch: refetchBasicEvents } = useQuery<
    IGetDictionaryBasicEventsQuery,
    IGetDictionaryBasicEventsQueryVariables
  >(GET_DICTIONARY_BASIC_EVENTS, {
    variables: {
      term: debouncedSearchQuery,
      schemaId: selectedSchemaId,
    },
    onCompleted: onFetchDictionaryBasicEvents,
  });

  const onDescriptionChange = async (
    basicEvent: IDictionaryBasicEventFieldsFragment,
    newValue: string,
  ) => {
    const res = await updateBasicEventDescription({
      variables: {
        input: {
          description: newValue.trim(),
          schema: selectedSchemaId,
          basicEventKey: basicEvent.key,
        },
      },
    });
    if (printGraphqlErrors(res?.data?.basicEventUpdateDescription?.errors)) {
      return;
    }

    toastMessage({
      message: `${basicEvent.name} has been updated`,
      type: 'info',
    });

    track(eventInformationUpdated(basicEvent));

    await refetchBasicEvents();
  };

  const handleSubmitNewTags = () => {
    refetchBasicEvents();
    setRowSelection({});
  };

  const handleApplyTags = useApplyTags({
    entityType: CategoryEntityType.BasicEvent,
    addCategoryMutation: BASIC_EVENT_ADD_CATEGORY,
    removeCategoryMutation: BASIC_EVENT_REMOVE_CATEGORY,
    managedEntityType: ManagedEntityType.BasicEvent,
    getEntityId: (entity: IDictionaryBasicEventFieldsFragment) => entity.key,
    getEntityCategories: (entity: IDictionaryBasicEventFieldsFragment) =>
      entity.entityCategories,
    onSuccess: handleSubmitNewTags,
  });

  const handleGlobalApplyTags = useGlobalApplyTags({
    rowSelection,
    entities: events,
    selectedSchemaId,
    handleApplyTags,
    getEntityId: (entity) => entity.key,
  });

  const selectedEntityCategories = events
    .filter((event) => rowSelection[event.key])
    .map((event) => ({ categories: event.entityCategories }));

  return (
    <>
      <DictionaryHeader
        selectedSchemaId={selectedSchemaId}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        title={'Event'}
        dictionaryItemCount={events.length}
        permittedSchemaIds={permittedSchemaIds}
        onApplyTags={handleGlobalApplyTags}
        isPermittedToAssignCategory={isPermittedToAssignCategory}
        showApplyTags={Object.keys(rowSelection).length > 0}
        selectedEntities={selectedEntityCategories}
      />

      {basicEventsLoading ? (
        <ContentLoader />
      ) : events.length === 0 ? (
        <DictionaryNoResults entityType={DictionaryTypeEnum.DictionaryEvent} />
      ) : (
        <EventsTable
          events={events}
          schemaId={selectedSchemaId}
          onApplyTags={handleApplyTags}
          onRenameEvent={onRenameEventModalConfirm}
          onDescriptionChange={onDescriptionChange}
          onToggleEvent={setEventStatusCallback}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          isPermittedToEditEvent={isPermittedToEditEvent}
          isPermittedToAssignCategory={isPermittedToAssignCategory}
          preventBasicEventMetadataUpdates={preventBasicEventMetadataUpdates}
        />
      )}
    </>
  );
};

export default EventDictionary;
